import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Card, CardHeader } from "@/components/ui/Card";
import { Label } from "@/components/ui/Label";
import { toast } from "sonner";
import { formatPhoneDisplay, PHONE_MASK } from "@/hooks/useDemographicsForm";
import { cn } from "@/utils/cn";

const EMERGENCY_FIELDS = ["emerContact", "emerRelation", "emerPhone"] as const;

const ERROR_INPUT_CLASS = "border-red-500 focus:ring-red-500";

// Below sm the section card is flattened onto the page background so the fields run to the
// page gutter; overflow stays visible so input focus rings at the edge are not clipped.
// `!` beats Card's inline shadow.
const FLAT_BELOW_SM =
  "max-sm:overflow-visible max-sm:rounded-none max-sm:border-0 max-sm:bg-transparent max-sm:!shadow-none";

// Read-only fields: compact label/value rows with hairline dividers below sm (a long value wraps
// under its label); stacked label over value in the grid from sm.
const READONLY_ROW_CLASS =
  "flex min-h-11 min-w-0 flex-wrap items-center justify-between gap-x-4 border-b border-[var(--hs-line)] py-2 sm:block sm:min-h-0 sm:border-0 sm:py-0";
const READONLY_VALUE_CLASS =
  "ml-auto min-w-0 break-words text-right text-[15px] font-bold text-healthsource-dark sm:ml-0 sm:text-left";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-semibold text-red-600">{message}</p>;
}

export interface DemographicsEmergencySectionProps {
  patient: Record<string, string>;
  update: (field: string, value: string) => void;
  errors?: Record<string, string>;
  onSave: () => Promise<boolean>;
  revertFields: (fields: Record<string, string>) => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** When set, edit state is controlled by parent (e.g. profile tab bar). */
  editingOverride?: boolean;
  /** When true, do not render Edit/Cancel/Update in the card header. */
  hideHeaderActions?: boolean;
  emerRelationOpts: { value: string; label: string }[];
  /** Wizard/mobile safe layout: render one field per row. */
  singleColumn?: boolean;
}

export function DemographicsEmergencySection({
  patient,
  update,
  errors,
  onSave,
  revertFields,
  onDirtyChange,
  editingOverride,
  hideHeaderActions,
  emerRelationOpts,
  singleColumn = false,
}: DemographicsEmergencySectionProps) {
  const [internalEditing, setInternalEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const snapshotRef = useRef<Record<string, string>>({});

  const editing = editingOverride !== undefined ? editingOverride : internalEditing;

  const snapshot = useCallback(() => {
    const s: Record<string, string> = {};
    for (const k of EMERGENCY_FIELDS) s[k] = patient[k] ?? "";
    return s;
  }, [patient]);

  useEffect(() => {
    if (editingOverride === true) snapshotRef.current = snapshot();
  }, [editingOverride, snapshot]);

  const isDirty = editing && EMERGENCY_FIELDS.some(
    (k) => (patient[k] ?? "") !== (snapshotRef.current[k] ?? "")
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const startEditing = () => {
    snapshotRef.current = snapshot();
    if (editingOverride === undefined) setInternalEditing(true);
  };

  const revert = () => {
    revertFields(snapshotRef.current);
  };

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      if (editingOverride === undefined) setInternalEditing(false);
    }
  };

  const confirmDiscard = () => {
    revert();
    setShowDiscardModal(false);
    if (editingOverride === undefined) setInternalEditing(false);
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await onSave();
      if (editingOverride === undefined) setInternalEditing(false);
    } catch {
      toast.error("Failed to update emergency contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className={cn("mb-3", FLAT_BELOW_SM)}>
        <CardHeader
          className="max-sm:px-0"
          action={
            hideHeaderActions ? null : !editing ? (
              <button
                type="button"
                onClick={startEditing}
                className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-navy transition-colors hover:text-healthsource-red"
              >
                <i className="fas fa-pen" />
                Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex min-h-11 items-center gap-1.5 px-3 text-sm font-semibold text-healthsource-gray transition-colors hover:text-healthsource-red"
                >
                  Cancel
                </button>
                {isDirty && (
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={saving}
                    className="min-h-11 rounded-md px-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: "var(--hs-red)" }}
                  >
                    {saving ? "Saving…" : "Update"}
                  </button>
                )}
              </div>
            )
          }
        >
          Emergency Contact
        </CardHeader>

        {!editing ? (
          <div
            className="grid grid-cols-1 py-1 sm:grid-cols-3 sm:gap-x-6 sm:gap-y-3 sm:p-5"
          >
            <Field label="Name" value={patient.emerContact} />
            <Field label="Relationship" value={patient.emerRelation} />
            <Field
              label="Phone"
              value={formatPhoneDisplay((patient.emerPhone ?? "").replace(/\D/g, ""))}
            />
          </div>
        ) : (
          <div
            className={
              singleColumn
                ? "grid min-w-0 grid-cols-1 gap-3 py-3 sm:p-5 md:grid-cols-3"
                : "grid min-w-0 grid-cols-1 gap-3 py-3 sm:p-5 md:grid-cols-3"
            }
          >
            <div className="min-w-0">
              <Label htmlFor="demographics-emerContact" required>Name</Label>
              <Input
                id="demographics-emerContact"
                placeholder="Name"
                value={patient.emerContact}
                onChange={(e) => update("emerContact", e.target.value)}
                className={cn(errors?.emerContact && ERROR_INPUT_CLASS)}
                aria-invalid={errors?.emerContact ? true : undefined}
                required
              />
              <FieldError message={errors?.emerContact} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="demographics-emerRelation" required>Relationship</Label>
              <Select
                id="demographics-emerRelation"
                className={cn(errors?.emerRelation && ERROR_INPUT_CLASS)}
                value={patient.emerRelation}
                onChange={(e) => update("emerRelation", e.target.value)}
                aria-invalid={errors?.emerRelation ? true : undefined}
                required
              >
                <option value="">Select</option>
                {emerRelationOpts.map((o, i) => (
                  <option key={`${o.value}-${i}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              <FieldError message={errors?.emerRelation} />
            </div>
            <div className="min-w-0">
              <Label htmlFor="demographics-emerPhone" required>Phone</Label>
              <Input
                id="demographics-emerPhone"
                type="tel"
                placeholder="(XXX) XXX-XXXX"
                value={patient.emerPhone}
                onChange={(e) =>
                  update("emerPhone", formatPhoneDisplay(PHONE_MASK(e.target.value)))
                }
                className={cn(errors?.emerPhone && ERROR_INPUT_CLASS)}
                aria-invalid={errors?.emerPhone ? true : undefined}
                required
              />
              <FieldError message={errors?.emerPhone} />
            </div>
          </div>
        )}
      </Card>

      {showDiscardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90dvh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 shadow-lg" style={{ boxShadow: "0 8px 32px rgba(30,37,43,0.18)" }}>
            <h3 className="text-[15px] font-extrabold text-healthsource-dark">Unsaved Changes</h3>
            <p className="mt-2 text-[13px] text-healthsource-gray">You have unsaved changes. Are you sure you want to discard them?</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDiscardModal(false)}
                className="min-h-11 rounded-md border border-healthsource-lightLavender px-4 text-[13px] font-bold text-healthsource-dark transition-colors hover:bg-healthsource-lightGray"
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={confirmDiscard}
                className="min-h-11 rounded-md px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--hs-red)" }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className={READONLY_ROW_CLASS}>
      <p className="text-[13px] font-semibold text-healthsource-gray">{label}</p>
      <p className={READONLY_VALUE_CLASS}>{value || "—"}</p>
    </div>
  );
}
